<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import MultiSelect from 'primevue/multiselect';
import { useToast } from 'primevue/usetoast';
import Page from '@/shared/components/layouts/Page.vue';
import { useLoadSheetCommit } from '../../composables/useLoadSheetCommit';
import CommitStatistics from '../../components/CommitStatistics.vue';
import CommitTimeline from '../../components/CommitTimeline.vue';
import { ValidationRulesService } from '@/shared/services/validationRules';
import {
  getCommits,
  type CommitFilterParams,
  type LoadSheetCommitDto,
} from '@/shared/services/loadSheetCommitService';

definePage({
  meta: {
    title: 'Load Sheet Import History',
  },
});

const SERVER_PAGE_SIZE = 100;

const toast = useToast();
const {
  statistics,
  isLoadingStatistics,
  fetchStatistics,
} = useLoadSheetCommit({ autoConnect: false });

const allCommits = ref<LoadSheetCommitDto[]>([]);
const selectedEntityTypes = ref<string[]>([]);
const selectedCommitType = ref('');
const entityTypeOptions = ref<string[]>([]);
const isLoadingCommits = ref(false);
const isLoadingEntityTypes = ref(false);
let latestCommitRequest = 0;

const commitTypeOptions = computed<{ label: string; value: string }[]>(() => {
  const types = [...new Set(allCommits.value.map((c) => c.commitType))].sort();
  return [
    { label: 'All', value: '' },
    ...types.map((type) => ({ label: type, value: type })),
  ];
});

const filteredCommits = computed(() => {
  let result = allCommits.value;
  // Client-side entity type filter (active when 2+ types selected; single selection is server-side)
  if (selectedEntityTypes.value.length > 1) {
    result = result.filter((c) => selectedEntityTypes.value.includes(c.entityTypeName));
  }
  if (selectedCommitType.value) {
    result = result.filter((c) => c.commitType === selectedCommitType.value);
  }
  return result;
});

function buildQueryParams(nextPage = 1): CommitFilterParams {
  return {
    // Use server-side filter only for single selection; multi-selection is filtered client-side
    entityType: selectedEntityTypes.value.length === 1 ? selectedEntityTypes.value[0] : undefined,
    sortBy: 'committedAtUtc',
    sortDirection: 'desc',
    page: nextPage,
    pageSize: SERVER_PAGE_SIZE,
  };
}

async function fetchCommitHistory(): Promise<void> {
  const requestId = ++latestCommitRequest;
  isLoadingCommits.value = true;

  try {
    const commits: LoadSheetCommitDto[] = [];
    let nextPage = 1;
    let totalPagesToFetch = 1;

    do {
      const result = await getCommits(buildQueryParams(nextPage));

      if (requestId !== latestCommitRequest) {
        return;
      }

      commits.push(...result.commits);
      totalPagesToFetch = result.pageSize > 0
        ? Math.max(Math.ceil(result.totalCount / result.pageSize), 1)
        : 1;
      nextPage += 1;
    } while (nextPage <= totalPagesToFetch);

    allCommits.value = commits;
  } catch (error: unknown) {
    if (requestId !== latestCommitRequest) {
      return;
    }

    allCommits.value = [];
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load import history. Please try again.',
      life: 5000,
    });
  } finally {
    if (requestId === latestCommitRequest) {
      isLoadingCommits.value = false;
    }
  }
}

async function handleEntityTypeChange(value: string[]): Promise<void> {
  selectedEntityTypes.value = value ?? [];
  selectedCommitType.value = '';
  await fetchCommitHistory();
}

async function loadEntityTypes(): Promise<void> {
  isLoadingEntityTypes.value = true;

  try {
    const entityTypes = await ValidationRulesService.getEntityTypes();
    entityTypeOptions.value = entityTypes.map((e) => e.name ?? '').filter(Boolean);
  } catch (error: unknown) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load entity types. Showing all import history instead.',
      life: 5000,
    });
    entityTypeOptions.value = [];
  } finally {
    isLoadingEntityTypes.value = false;
  }
}

onMounted(async () => {
  await Promise.all([loadEntityTypes(), fetchCommitHistory(), fetchStatistics()]);
});
</script>

<template>
  <Page>
    <div class="flex flex-col gap-2 min-h-0 flex-1">
      <section class="shrink-0 bg-background-card rounded-lg shadow-card overflow-hidden px-4 py-3">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="flex items-center gap-3">
            <div class="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-text-inverse shadow-xs shrink-0">
              <i class="fa-solid fa-clock-rotate-left text-sm"></i>
            </div>

            <div>
              <h1 class="text-base font-bold text-primary leading-tight">Import History</h1>
              <p class="text-xs text-secondary">Review load sheet import activity in a single timeline view.</p>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <label class="text-xs font-medium text-text-muted shrink-0">Entity Type</label>
            <MultiSelect
              :modelValue="selectedEntityTypes"
              :options="entityTypeOptions"
              placeholder="All Entity Types"
              class="w-52"
              :filter="true"
              display="chip"
              :loading="isLoadingEntityTypes"
              @update:modelValue="handleEntityTypeChange"
            />
          </div>
        </div>
      </section>

      <div class="flex-1 min-h-0 grid gap-2 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <section class="min-w-0 flex flex-col min-h-0 bg-background-card rounded-lg shadow-card overflow-hidden">
          <div class="shrink-0 flex flex-wrap items-center justify-between gap-3 border-b border-border bg-primary/10 px-4 py-3">
            <div>
              <h2 class="text-sm font-bold text-primary">Browse imports</h2>
              <p class="text-xs text-secondary">
                Review chronological import activity{{ selectedEntityTypes.length > 0 ? ' for selected entity types' : '' }}.
              </p>
            </div>

            <div v-if="commitTypeOptions.length > 2" class="flex items-center gap-1 rounded-full bg-background p-0.5 shadow-xs">
              <button
                v-for="option in commitTypeOptions"
                :key="option.value"
                class="px-2.5 py-0.5 text-xs rounded-full transition-colors"
                :class="selectedCommitType === option.value
                  ? 'bg-primary text-text-inverse font-medium shadow-xs'
                  : 'text-secondary hover:text-primary'"
                @click="selectedCommitType = option.value"
              >
                {{ option.label }}
              </button>
            </div>
          </div>

          <div class="flex-1 min-h-0 overflow-y-auto">
            <div v-if="isLoadingCommits" class="flex min-h-96 items-center justify-center">
              <i class="pi pi-spin pi-spinner text-2xl text-text-muted" />
            </div>

            <div
              v-else-if="filteredCommits.length === 0"
              class="flex min-h-96 flex-col items-center justify-center gap-3 px-6 text-center"
            >
              <div class="flex h-12 w-12 items-center justify-center rounded-full bg-background-subtle text-primary">
                <i class="pi pi-inbox text-lg" />
              </div>
              <div class="space-y-1">
                <h3 class="text-sm font-semibold text-secondary">No imports to display</h3>
                <p class="text-sm text-text-muted">
                  {{
                    allCommits.length === 0
                      ? (selectedEntityTypes.length > 0
                          ? `No imports were found for ${selectedEntityTypes.join(', ')}.`
                          : 'No imports are available for this history view.')
                      : `No ${selectedCommitType} imports found. Try switching to All.`
                  }}
                </p>
              </div>
            </div>

            <CommitTimeline v-else :commits="filteredCommits" />
          </div>
        </section>

        <aside class="min-w-0 overflow-y-auto">
          <CommitStatistics
            :statistics="statistics"
            :is-loading="isLoadingStatistics"
          />
        </aside>
      </div>
    </div>
  </Page>
</template>
